import { useQuery } from "@apollo/client";
import IndicatorsQuery from "components/financials/modern/graphql/IndicatorsQuery";
import type {
  IIndicatorsQuery,
  IIndicatorsQueryVariables,
} from "components/financials/modern/graphql/__generated__/IndicatorsQuery.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";

const cache = new Map<string, IIndicatorFieldsFragment>();
const isServer = typeof window === "undefined";

export default function useIndicatorsByObjectId(objectIds: string[]) {
  const fullMatch = objectIds.every((objectId) => cache.has(objectId));
  const missingObjectIds = objectIds.filter((objectId) => !cache.has(objectId));

  const { data, previousData, loading, ...rest } = useQuery<IIndicatorsQuery, IIndicatorsQueryVariables>(
    IndicatorsQuery,
    {
      variables: {
        objectIds: missingObjectIds,
        filters: null,
      },
    },
  );

  const indicators = (loading && !data ? previousData?.indicators : data?.indicators) || [];

  if (isServer) {
    return {
      ...rest,
      loading,
      indicators,
    };
  }

  if (fullMatch) {
    return {
      ...rest,
      loading: false,
      indicators: objectIds.map((objectId) => cache.get(objectId) as IIndicatorFieldsFragment),
    };
  }

  indicators.forEach((indicator) => {
    cache.set(indicator.objectId, indicator);
  });

  return {
    ...rest,
    loading,
    indicators: objectIds
      .map((candidate) => {
        return cache.get(candidate);
      })
      .filter(notUndefinedOrNull),
  };
}
